/** Read-only Web Serial facade for the RFID reader's CP210x USB bridge.
 * Register definitions: Linux drivers/usb/serial/cp210x.c (Silicon Labs protocol).
 */
export interface UsbEndpoint { direction: string; type: string; endpointNumber: number; packetSize: number }
export interface UsbAlternate { alternateSetting: number; interfaceClass: number; endpoints: UsbEndpoint[] }
export interface UsbDevice {
  vendorId: number;
  productId: number;
  opened: boolean;
  configuration: { interfaces: { interfaceNumber: number; alternates: UsbAlternate[] }[] } | null;
  configurations: { configurationValue: number }[];
  open(): Promise<void>;
  close(): Promise<void>;
  selectConfiguration(value: number): Promise<void>;
  claimInterface(value: number): Promise<void>;
  selectAlternateInterface(index: number, alternate: number): Promise<void>;
  controlTransferOut(setup: { requestType: "vendor"; recipient: "interface"; request: number; value: number; index: number }, data?: BufferSource): Promise<{ status: string }>;
  transferIn(endpoint: number, length: number): Promise<{ status: string; data?: DataView }>;
}
export interface UsbApi {
  requestDevice(options: { filters: { vendorId: number; productId: number }[] }): Promise<UsbDevice>;
  getDevices(): Promise<UsbDevice[]>;
  addEventListener(type: string, listener: (event: { device: UsbDevice }) => void): void;
  removeEventListener(type: string, listener: (event: { device: UsbDevice }) => void): void;
}
export interface RfidPort {
  readonly readable: ReadableStream<Uint8Array> | null;
  open(options: { baudRate: number }): Promise<void>;
  close(): Promise<void>;
}
export interface RfidTransport {
  getPorts(): Promise<RfidPort[]>;
  requestPort(): Promise<RfidPort>;
  subscribeDisconnect(callback: () => void): () => void;
}
const filters = [0xea60, 0xea70, 0xea71].map(productId => ({ vendorId: 0x10c4, productId }));
const matches = (device: UsbDevice) => filters.some(f => f.vendorId === device.vendorId && f.productId === device.productId);

export class Cp210xPort implements RfidPort {
  readable: ReadableStream<Uint8Array> | null = null;
  private closed = true;
  private closing: Promise<void> | null = null;
  private index = 0;
  constructor(readonly device: UsbDevice) {}
  private async control(request: number, value: number, data?: BufferSource) {
    const result = await this.device.controlTransferOut({ requestType: "vendor", recipient: "interface", request, value, index: this.index }, data);
    if (result.status !== "ok") throw new Error(`CP210x 설정 실패 (${request}: ${result.status})`);
  }
  async open({ baudRate }: { baudRate: number }) {
    if (!this.closed) throw new Error("RFID 리더기가 이미 연결되어 있습니다.");
    if (baudRate !== 9600) throw new Error("RFID 리더기는 9600 baud로 연결해야 합니다.");
    if (!matches(this.device)) throw new Error("지원하는 CP210x 리더기가 아닙니다.");
    await this.closing;
    this.closed = false;
    try {
      await this.device.open();
      if (!this.device.configuration) await this.device.selectConfiguration(this.device.configurations[0]?.configurationValue ?? 1);
      const candidates = this.device.configuration?.interfaces.flatMap(i => i.alternates.map(a => ({ i, a }))) ?? [];
      const match = candidates.find(({ a }) => a.interfaceClass === 255 && a.endpoints.some(e => e.type === "bulk" && e.direction === "in") && a.endpoints.some(e => e.type === "bulk" && e.direction === "out"));
      if (!match) throw new Error("CP210x 시리얼 인터페이스를 찾지 못했습니다.");
      this.index = match.i.interfaceNumber;
      await this.device.claimInterface(this.index);
      await this.device.selectAlternateInterface(this.index, match.a.alternateSetting);
      await this.control(0x00, 1); // UART enable
      const rate = new DataView(new ArrayBuffer(4));
      rate.setUint32(0, baudRate, true);
      await this.control(0x1e, 0, rate); // baud rate, little endian
      await this.control(0x03, 0x0800); // 8 data bits, no parity, 1 stop bit
      const flow = new DataView(new ArrayBuffer(16));
      flow.setUint32(0, 1, true); // DTR active; no CTS/DSR handshaking
      flow.setUint32(4, 0x40, true); // RTS active; no software flow control
      await this.control(0x13, 0, flow);
      await this.control(0x07, 0x0303); // set DTR and RTS
      const endpoint = match.a.endpoints.find(e => e.type === "bulk" && e.direction === "in")!;
      this.readable = new ReadableStream<Uint8Array>({
        pull: async controller => {
          try {
            const result = await this.device.transferIn(endpoint.endpointNumber, endpoint.packetSize);
            if (this.closed) return;
            if (result.status !== "ok") throw new Error(`RFID USB 수신 실패 (${result.status})`);
            if (result.data?.byteLength) controller.enqueue(new Uint8Array(result.data.buffer, result.data.byteOffset, result.data.byteLength));
          } catch (error) {
            if (!this.closed) controller.error(error);
          }
        },
        // Closing the USB device aborts a pending transferIn, so cancel never waits for a tag.
        cancel: () => this.close(),
      });
    } catch (error) {
      await this.close().catch(() => undefined);
      throw error;
    }
  }
  async close() {
    if (this.closing) return this.closing;
    this.closed = true;
    this.readable = null;
    this.closing = this.device.opened ? this.device.close() : Promise.resolve();
    try { await this.closing; } finally { this.closing = null; }
  }
}

export function cp210xTransport(usb: UsbApi): RfidTransport {
  return {
    async getPorts() { return (await usb.getDevices()).filter(matches).map(device => new Cp210xPort(device)); },
    async requestPort() { return new Cp210xPort(await usb.requestDevice({ filters })); },
    subscribeDisconnect(callback) {
      const listener = ({ device }: { device: UsbDevice }) => { if (matches(device)) callback(); };
      usb.addEventListener("disconnect", listener);
      return () => usb.removeEventListener("disconnect", listener);
    },
  };
}

export function getRfidTransport(): RfidTransport | null {
  if (typeof navigator === "undefined" || !globalThis.isSecureContext) return null;
  const usb = (navigator as Navigator & { usb?: UsbApi }).usb;
  // Android's newer native Serial API may expose Bluetooth only; USB RFID still uses WebUSB.
  if (usb && (/Android/i.test(navigator.userAgent) || !navigator.serial)) return cp210xTransport(usb);
  const serial = navigator.serial;
  if (!serial) return null;
  return {
    getPorts: () => serial.getPorts(), requestPort: () => serial.requestPort(),
    subscribeDisconnect(callback) {
      serial.addEventListener("disconnect", callback);
      return () => serial.removeEventListener("disconnect", callback);
    },
  };
}
